/*
  fs-duo-offer.js
  Behaviour for sections/fs-duo-offer.liquid (Duo offer PDP):
  - grind (option) selection drives the real variant id, prices and labels
  - every add button submits the one product form over AJAX and opens the
    theme's cart drawer (falls back to a normal form post without it)
  - sticky add bar once the buy-box button scrolls under the header
  - keeps the offer line in the announcement bar slot after editor re-renders
*/
if (!customElements.get('fs-duo-offer')) {
  customElements.define(
    'fs-duo-offer',
    class FsDuoOffer extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form[data-duo-form]');
        this.idInput = this.querySelector('[data-duo-variant-id]');
        this.optionInputs = Array.from(this.querySelectorAll('[data-duo-option]'));
        this.addButtons = Array.from(this.querySelectorAll('[data-duo-add]'));
        this.errorEl = this.querySelector('[data-duo-error]');
        this.sticky = this.querySelector('[data-duo-sticky]');
        this.anchor = this.querySelector('[data-duo-anchor]');
        this.addedMs = parseInt(this.dataset.addedMs, 10) || 1600;
        this.variants = this.readVariants();
        this.currentVariant = this.variants.find((v) => this.idInput && String(v.id) === this.idInput.value) || null;

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.requestStickyUpdate = this.requestStickyUpdate.bind(this);

        this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));
        if (this.form) this.form.addEventListener('submit', this.onSubmit);

        this.placePromo();
        this.initSticky();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.requestStickyUpdate);
        window.removeEventListener('resize', this.requestStickyUpdate);
        if (this.drawerObserver) this.drawerObserver.disconnect();
        if (this.addedTimer) clearTimeout(this.addedTimer);
        document.body.classList.remove('fs-duo-sticky-on');
      }

      readVariants() {
        const el = this.querySelector('[data-duo-variants]');
        if (!el) return [];
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return [];
        }
      }

      fill(template, variant) {
        const values = {
          price: variant.price,
          was: variant.was,
          saving: variant.saving,
          variant: variant.title,
        };
        return template.replace(/\(\((\w+)\)\)/g, (match, key) => (key in values ? values[key] : match));
      }

      /* ---------- Variant selection ---------- */
      onOptionChange() {
        const selected = {};
        this.optionInputs.forEach((input) => {
          if (input.checked) selected[input.dataset.position] = input.value;
        });
        const variant = this.variants.find((v) =>
          v.options.every((value, index) => {
            const chosen = selected[index + 1];
            return chosen === undefined || chosen === value;
          })
        );
        if (!variant) return;

        this.currentVariant = variant;
        if (this.idInput) this.idInput.value = variant.id;
        this.hideError();
        this.render(variant);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      render(variant) {
        this.querySelectorAll('[data-duo-tpl]').forEach((el) => {
          el.textContent = this.fill(el.dataset.duoTpl, variant);
        });
        this.querySelectorAll('[data-duo-if-saving]').forEach((el) => {
          el.hidden = !variant.hasSaving;
        });
        this.addButtons.forEach((button) => this.setButtonLabel(button, false));
      }

      setButtonLabel(button, added) {
        const variant = this.currentVariant;
        const text = button.querySelector('[data-duo-add-text]') || button;
        if (variant && !variant.available) {
          button.disabled = true;
          text.textContent = button.dataset.duoSoldout || 'Sold out';
          return;
        }
        button.disabled = false;
        if (added) {
          text.textContent = button.dataset.duoAdded;
        } else if (variant) {
          text.textContent = this.fill(button.dataset.duoLabel, variant);
        }
      }

      /* ---------- Add to cart ---------- */
      onSubmit(event) {
        const cart = document.querySelector('cart-drawer');
        if (!cart || typeof cart.renderContents !== 'function' || typeof cart.getSectionsToRender !== 'function') return;

        event.preventDefault();
        if (this.busy) return;
        this.busy = true;
        this.hideError();
        this.addButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));

        const formData = new FormData(this.form);
        formData.append(
          'sections',
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(event.submitter || document.activeElement);
        }

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'fs-duo-offer',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.showAdded();
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch((error) => {
            console.error('fs-duo-offer: add to cart failed', error);
            this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.busy = false;
            this.addButtons.forEach((button) => button.removeAttribute('aria-busy'));
          });
      }

      showAdded() {
        if (this.addedTimer) clearTimeout(this.addedTimer);
        this.addButtons.forEach((button) => this.setButtonLabel(button, true));
        this.addedTimer = setTimeout(() => {
          this.addButtons.forEach((button) => this.setButtonLabel(button, false));
          this.addedTimer = null;
        }, this.addedMs);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      /* ---------- Sticky add bar ---------- */
      initSticky() {
        if (!this.sticky || !this.anchor) return;
        window.addEventListener('scroll', this.requestStickyUpdate, { passive: true });
        window.addEventListener('resize', this.requestStickyUpdate);
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          this.drawerObserver = new MutationObserver(this.requestStickyUpdate);
          this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
        this.updateSticky();
      }

      requestStickyUpdate() {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = 0;
          this.updateSticky();
        });
      }

      updateSticky() {
        const headerHeight =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const drawer = document.querySelector('cart-drawer');
        const drawerOpen = !!(drawer && drawer.classList.contains('active'));
        const show = this.anchor.getBoundingClientRect().bottom < headerHeight && !drawerOpen;
        if (show === this.stickyShown) return;

        this.stickyShown = show;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) {
          this.sticky.removeAttribute('inert');
          document.body.style.setProperty('--duo-sticky-height', `${this.sticky.offsetHeight}px`);
        } else {
          this.sticky.setAttribute('inert', '');
        }
        document.body.classList.toggle('fs-duo-sticky-on', show);
      }

      /* ---------- Offer line in the announcement bar slot ---------- */
      placePromo() {
        const promo = this.querySelector('[data-duo-promo][data-replace]');
        const bar = document.querySelector('.announcement-bar-section');
        if (!promo || !bar || !bar.parentNode) return;
        document.querySelectorAll('[data-duo-promo][data-placed]').forEach((node) => node.remove());
        promo.setAttribute('data-placed', '');
        bar.parentNode.insertBefore(promo, bar);
      }
    }
  );
}
